import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Input, OnInit, OnChanges, SimpleChanges, AfterViewInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { orderBy } from 'lodash';
import { Subscription, distinctUntilChanged, forkJoin, combineLatest, tap, pipe } from 'rxjs';
import { BeachSite } from 'src/app/models/BeachSite';
import { PartnerDetailsModel } from 'src/app/models/BeachwatchPartners/PartnerDetailsModel';
import { PartnerGroupModel } from 'src/app/models/BeachwatchPartners/PartnerGroupModel';

import { RESPONSIVE_MOBILE_BREAKPOINT } from 'src/app/models/app-constants';
import { BeachwatchPartnersService } from 'src/app/services/beachwatch-partners.service';
import { SitesService } from 'src/app/services/sites.service';
import { BeachwatchPartnersLatestReportsLinks } from '../Beachwatch-Partners-Latest-Reports';
import { STATE_OF_BEACHES_REPORT_URL_EXTERNAL } from 'src/app/models/external-urls';
import { AppMonitoringService } from 'src/app/services/logging.service';

@Component({
	selector: 'app-partner-details',
	templateUrl: './partner-details.component.html',
	styleUrls: ['./partner-details.component.scss']
})
export class PartnerDetailsComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	
	public partnerIdentifier : string; // change to private after partner details api starts working
	public partnerDetails : PartnerDetailsModel;
	public allPartners : PartnerGroupModel[] = [];
	public allBeachSites : BeachSite[] = [];
	public partnerLatestReportLink : string;
	private navSub : Subscription;
	private breakpointSub : Subscription;
	private apiRequestsSub : Subscription;
	isMobileView : boolean = false;
	mobileMenuExpanded : boolean = false;


	public STATE_OF_BEACHES_REPORT_URL = STATE_OF_BEACHES_REPORT_URL_EXTERNAL;

	readonly breakpoint$ = this.breakpointObserver.observe(RESPONSIVE_MOBILE_BREAKPOINT).pipe(tap(value => {
      
    }), distinctUntilChanged());

	constructor(private router: Router,
		private activatedRoute : ActivatedRoute,
		private beachwatchPartnersService : BeachwatchPartnersService,
		private sitesService : SitesService,
		private breakpointObserver : BreakpointObserver,
		private appMonitoring: AppMonitoringService) 
	{		
		this.navSub = this.router.events.subscribe({
			next : (e : any) => {
				if (e instanceof NavigationEnd) 
				{
					this.onNavigationEnd();
				}				
			},
			error : (ex) => {
				console.log(ex);
			},
			complete : () => {

			}
		});
	}

	ngOnInit(): void {
		this.navSub = this.activatedRoute.paramMap.subscribe({
			next : (params) => {				
				this.partnerIdentifier = params.get('id') || "";
				this.partnerLatestReportLink = "";
				this.getData();
			},
			error : (ex) => {
				console.log(`[Exception reading partner identifier from the route : ${ex?.message ?? ""} ]`);
			},
			complete : () => {
				
			}
		});
		
		this.breakpointSub = this.breakpoint$.subscribe({
            next: (result) => {
				this.isMobileView = result.matches;
				if(!result.matches)
				{
					this.mobileMenuExpanded = false;
				}
            },
            error: (ex) => {

            },
            complete: () => {

            }
        });
	}

	ngOnChanges(changes: SimpleChanges): void {
		
	}

	ngAfterViewInit(): void {
		
	}

	ngOnDestroy(): void {
		this.apiRequestsSub && this.apiRequestsSub.unsubscribe();
		this.navSub && this.navSub.unsubscribe();
		this.breakpointSub && this.breakpointSub?.unsubscribe();		
	}

	navigateToStateOfBeaches(): void {
		// track the site that was clicked from the GlobalSearch
		this.appMonitoring.logEvent('StateOfTheBeachesReportClick',{})
    }

	/* 
		The beachwatch partners page passes the clicked partner and all partners  as a state object so that we don't have to get the partners  (in the partner detail page)
		everytime user clicks on a different partner.
		Watch for router event navigation end routerevent to grab the state because we are reloading the same page (partner detail page) from the same page 
		but with a different partner. 
		Router event is used in conjunction with router config property onSameUrlNavigation : 'reload' (app.routing.module) and runGuardsAndResolvers : 'always' on the route path in Routes array. 
	*/
	private onNavigationEnd = () => {
		
		// this.partnerIdentifier = this.router.getCurrentNavigation()?.id?.toString() ?? "";		
		
		const routeState = this.router.getCurrentNavigation()?.extras.state;
		//const routeState = this.location.getState();
		if(routeState)
		{	
			if(Array.isArray((<any>routeState)?.allPartners))
			{
				this.allPartners = (<any>routeState).allPartners;
			}
			if(Array.isArray((<any>routeState)?.allBeachSites))
			{
				this.allBeachSites = (<any>routeState).allBeachSites;
			}					
		}		
	}

	private getData = () => {
		if(this.partnerIdentifier)
		{
			const allRequests = [
				this.beachwatchPartnersService.getPartnerDetails(this.partnerIdentifier)
			];		
			
			let pIdx = -1, bIdx = -1;

			if(!Array.isArray(this.allPartners) || 0 === this.allPartners?.length)
			{				
				allRequests.push(this.beachwatchPartnersService.getPartnerGroups());
				pIdx = allRequests.length - 1;
			}

			if(!Array.isArray(this.allBeachSites) || 0 === this.allBeachSites?.length)
			{				
				allRequests.push(this.sitesService.getSitesApi());
				bIdx = allRequests.length - 1;
			}		

			this.apiRequestsSub = combineLatest(allRequests).subscribe({
				next : (response) => {
					if(Array.isArray(response))
					{
						this.partnerDetails = { ...response[0] };					
						
						if(0 < pIdx && response.length >= pIdx + 1)
						{
							this.allPartners = response[pIdx]?.map((partner : PartnerGroupModel) => ({ ...partner }));
						}
						if(0 < bIdx && response.length >= bIdx + 1)
						{
							this.allBeachSites = response[bIdx] ?? [];
						}					
						this.partnerLatestReportLink = BeachwatchPartnersLatestReportsLinks.find((r) => r.partner.toLowerCase() === this.partnerDetails?.PartnerName?.toLowerCase())?.latestReport ?? "";
					}
				},
				error : (ex : any) => {
					console.debug(`[Exception retrieving partner details : ${ex?.message ?? ""} ]`);
				},
				complete : () => {
					
				}
			});
			
		}
	}

	onExpandMobileMenuClicked = (event : any) => {
		this.mobileMenuExpanded = !this.mobileMenuExpanded;
	}

	onDummyLinkClicked = (event : any) => {
		event.preventDefault();
		return false;
	}

	get partnerSites() {
		if(Array.isArray(this.partnerDetails?.PartnerSites))
		{
			return this.partnerDetails?.PartnerSites?.sort((a, b) => {
				const siteA = this.allBeachSites?.find((site : BeachSite) => a.Identifier === site.Identifier);	
				const siteB = this.allBeachSites?.find((site : BeachSite) => b.Identifier === site.Identifier);	
				if(siteA && siteB)
				{
					return siteB.Lat - siteA.Lat;
				}
	
				return a.SiteName?.localeCompare(b.SiteName);
			});
		}

		return [];
		
	}	
}
